import { asCur } from "@/helpers/inputHelpers";
import { reactive, watchEffect } from "vue";
import useLeningdelen from "./useLeningdelen";
import type { MortgagePart } from "@/models/Leningdeel";
import { useParameters } from "@/store/useParameters";
import type { Belastingschijf } from "@/models/domain/Belastingschijf";
import type { Eigenwoningforfaitschijf } from "@/models/domain/Eigenwoningforfaitschijf";
import type { MonthlyPayment } from "@/models/domain/MonthlyPayment";
import { calculateTotalYearlyIncome } from "@/helpers/inventarisation/incomeCalculations";
import { useDossierDataStore } from "@/store/useDossierDataStore";
import { getMonthSpan } from "@loanlink-nl/shared";
import { storeToRefs } from "pinia";
import { useCalculationDate } from "@/composables/useCalculationDate";

export default function useMaandlasten() {
	const state = reactive({
		values: {} as { aftrekbareKosten?: number },
		gegevensPerJaar: {} as Record<number, MonthlyPayment>,
	});

	const { getMaandlast } = useLeningdelen();
	const { calculationDate, passingDate } = useCalculationDate();
	const dossierDataStore = useDossierDataStore();
	const { data } = storeToRefs(dossierDataStore);

	let currentYear = new Date().getFullYear();

	/**
	 * Calculate the deductible amount.
	 */
	watchEffect(() => {
		let aftrekbareKosten = 0;

		if (Number.isFinite(data.value.objectData.erfpacht?.canonYearly)) {
			aftrekbareKosten +=
				(data.value.objectData.erfpacht?.canonYearly as number) / 12;
		}

		state.values.aftrekbareKosten = aftrekbareKosten;
	});

	/**
	 * Calculate monthly payments for the full duration of the loan(s).
	 */
	watchEffect(() => {
		currentYear = calculationDate.value.year;

		const wozWaarde = asCur(useDossierDataStore().data.objectData.wozWaarde);

		state.gegevensPerJaar = {};
		for (let yearOffset = 0; yearOffset < 30; yearOffset++) {
			const yearOfLoop = currentYear + yearOffset;

			const monthlyPayments: MonthlyPayment = {
				jaar: `${yearOffset + 1}e jaar`,
				brutoMaandlast: 0,
				aflossing: 0,
				renteBedrag: 0,
				nettoMaandlast: 0,
				voordeel: 0,
				deductibleInterest: 0,
				box1Amount: 0,
			};

			for (const part of data.value.mortgage.mortgageParts) {
				const durationMonths =
					part.durationMonths ??
					(part.endDate
						? getMonthSpan(passingDate.value, part.endDate)
						: undefined);
				if (durationMonths !== undefined && yearOffset * 12 >= durationMonths) {
					continue;
				}

				const { maandlast, aflossing, renteBedrag } = getMaandlastSamenstelling(
					part,
					yearOffset,
				);

				monthlyPayments.brutoMaandlast += maandlast;
				monthlyPayments.aflossing += aflossing;
				monthlyPayments.renteBedrag += renteBedrag;

				const hoogteNum = asCur(part.amount) ?? 0;
				const isBox1 = part.interestDeductionLaw !== "none";

				if (isBox1 && Number.isFinite(hoogteNum)) {
					monthlyPayments.box1Amount += hoogteNum;
					monthlyPayments.deductibleInterest += renteBedrag;
				}
			}

			const nettoMaandlast = getNetMonthlyPayment(
				monthlyPayments.brutoMaandlast,
				monthlyPayments.deductibleInterest,
				monthlyPayments.box1Amount !== 0,
				wozWaarde ?? 0,
				`${yearOfLoop}-01-01`,
			);

			monthlyPayments.nettoMaandlast += nettoMaandlast;

			monthlyPayments.voordeel =
				monthlyPayments.brutoMaandlast - monthlyPayments.nettoMaandlast;

			state.gegevensPerJaar[yearOfLoop] = monthlyPayments;
		}
	});

	/**
	 * Get a loans composition
	 *
	 * @param part - The loan part
	 * @param yearsIntoLoan - Amount of years into the loan
	 * @returns An object with the monthly payment, the repayment per month and the interest amount.
	 */
	function getMaandlastSamenstelling(
		part: MortgagePart,
		yearsIntoLoan: number,
	) {
		let maandlast: number | undefined;
		let renteBedrag: number;
		let aflossing: number;

		const rente = asCur(part.interestRate) ?? NaN;
		const rentePerMaand = rente / 12;
		const hoogte = asCur(part.amount) ?? NaN;
		const durationMonths =
			part.durationMonths ??
			(part.endDate
				? getMonthSpan(passingDate.value, part.endDate)
				: undefined) ??
			NaN;

		switch (part.repaymentForm) {
			case "annuity": {
				const renteOpRentePeriode =
					Math.pow(1 + rentePerMaand, yearsIntoLoan * 12) - 1;
				const renteOpRenteLooptijd =
					Math.pow(1 + rentePerMaand, durationMonths) - 1;

				const nogAflossen =
					(1 - renteOpRentePeriode / renteOpRenteLooptijd) * hoogte;

				maandlast = getMaandlast(hoogte, rente, durationMonths);
				renteBedrag = nogAflossen * rentePerMaand;
				aflossing = maandlast - renteBedrag;

				break;
			}
			case "linear": {
				const reedsAfgelost = (hoogte / durationMonths) * (yearsIntoLoan * 12);
				const nogAflossen = hoogte - reedsAfgelost;

				aflossing = hoogte / durationMonths;
				renteBedrag = nogAflossen * rentePerMaand;
				maandlast = aflossing + renteBedrag;

				break;
			}
			default:
				aflossing = 0;
				renteBedrag = (hoogte * rente) / 12;
				maandlast = renteBedrag;
		}

		return { maandlast, aflossing, renteBedrag };
	}

	function getNetMonthlyPayment(
		grossPayment: number,
		interestAmount: number,
		box1: boolean,
		wozValue: number,
		date: string,
	): number {
		let netMonthlyPayment = grossPayment;

		if (box1 === true) {
			// Eligible for hypotheekrenteaftrek
			const incomeCalculation = calculateTotalYearlyIncome(
				data.value.income,
				data.value.customers.map((customer) => customer.id),
				calculationDate.value.toISODate() ?? "",
			);
			const incomeCustomer = incomeCalculation.customer1Income;
			const incomePartner = incomeCalculation.customer2Income;
			const highestIncome =
				incomeCustomer > incomePartner ? incomeCustomer : incomePartner;

			let highestIncomeWithBreaks = highestIncome;

			const eigenwoningforfaitschijven = useParameters(date).forfaitSchijven;
			let ewfHeight = calculateEWF(wozValue, eigenwoningforfaitschijven);
			const deductiblesAmount =
				(interestAmount + (state.values.aftrekbareKosten ?? 0)) * 12;

			if (!Number.isFinite(ewfHeight)) {
				ewfHeight = 0;
			}

			highestIncomeWithBreaks += ewfHeight;
			highestIncomeWithBreaks -= deductiblesAmount;

			const belastingSchijven = useParameters(date).inkomstenbelastingSchijven;
			const box1Bracket2 = belastingSchijven[1];

			const breakInBracket2 = Math.max(
				Math.min(
					highestIncome + ewfHeight - (box1Bracket2?.vanaf ?? 0),
					deductiblesAmount,
				),
				0,
			);

			const maximumDeductiblePercentage: number =
				useParameters(date).maximaleAftrek;

			const rateAdjustmentBox1 =
				breakInBracket2 *
				((box1Bracket2?.tarief ?? 0) - maximumDeductiblePercentage);

			const taxWithoutBreaks = computeBox1Tax(belastingSchijven, highestIncome);
			const taxWithBreaks =
				computeBox1Tax(belastingSchijven, highestIncomeWithBreaks) +
				rateAdjustmentBox1;

			const monthlyBenefits = (taxWithoutBreaks - taxWithBreaks) / 12;
			netMonthlyPayment = grossPayment - monthlyBenefits;
		}

		if (netMonthlyPayment > grossPayment) {
			netMonthlyPayment = grossPayment;
		}

		return netMonthlyPayment;
	}

	/**
	 * Compute Box 1 tax amount.
	 *
	 * @param belastingSchijven - The belastingSchijven to compute the tax for
	 * @param income - The pre-tax income
	 * @returns The tax that should be paid
	 */
	function computeBox1Tax(
		belastingSchijven: Belastingschijf[],
		income: number,
	): number {
		let totalTax = 0;
		let taxLeft = income;

		for (const bracket of belastingSchijven) {
			if (taxLeft <= 0) {
				break;
			}

			if ((bracket.tot ?? 0) > income || bracket.tot === undefined) {
				totalTax += taxLeft * bracket.tarief;
				break;
			} else if (bracket.tot <= income) {
				const taxedAmount = bracket.tot - (bracket.vanaf ?? 0);
				totalTax += taxedAmount * bracket.tarief;
				taxLeft -= taxedAmount;
			}
		}

		return totalTax;
	}

	/**
	 * Calculate the 'Eigenwoningforfait' based on the WOZ-value
	 *
	 * @param wozValue - The WOZ-value of the property to be bought
	 * @returns The value of the 'Eigenwoningforfait'
	 */
	function calculateEWF(
		wozValue: number,
		forfaitSchijven: Eigenwoningforfaitschijf[],
	): number {
		for (const bracket of forfaitSchijven) {
			const valueFallsInBracket =
				(bracket.vanaf ? wozValue > bracket.vanaf : true) &&
				(bracket.tot ? wozValue <= bracket.tot : true);

			if (valueFallsInBracket) {
				if (typeof bracket.forfait === "number") {
					return bracket.forfait * wozValue;
				}
				return bracket.forfait(wozValue);
			}
		}

		return 0;
	}

	return { state };
}
